import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { 
  Languages, Search, Loader2, Globe, ArrowLeft, Clock, 
  BookOpen, Volume2, FileText, GitBranch, User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE } from '../config/api';
const API = `${API_BASE}`;

const COVER_IMAGES = {
  'gradient-purple': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'gradient-blue': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'gradient-green': 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  'gradient-orange': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'gradient-pink': 'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)',
  'pattern-runes': 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  'pattern-glyphs': 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
  'pattern-waves': 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
  'abstract-language': 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)',
  'abstract-scripts': 'linear-gradient(135deg, #e65c00 0%, #f9d423 100%)',
};

const DiscoverPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [conlangs, setConlangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user) {
      fetchPublicConlangs();
    }
  }, [user]);

  const fetchPublicConlangs = async () => {
    try {
      const response = await axios.get(`${API}/public/conlangs`, { withCredentials: true });
      setConlangs(response.data);
    } catch (error) {
      toast.error('Failed to load public languages');
    } finally {
      setLoading(false);
    }
  };

  const filteredConlangs = conlangs.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.native_name && c.native_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (c.owner_name && c.owner_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-overlay">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-foreground-muted hover:text-foreground p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary/20 flex items-center justify-center">
                <Languages className="w-5 h-5 text-primary" />
              </div>
              <span className="font-heading text-xl font-semibold text-foreground">
                <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Globe className="w-8 h-8 text-primary" />
              <h1 className="font-heading text-3xl font-bold text-foreground" data-testid="discover-title">
                Discover Languages
              </h1>
            </div>
            <p className="text-foreground-muted">
              Explore languages shared by the community. {conlangs.length} public {conlangs.length === 1 ? 'language' : 'languages'} available.
            </p>
          </div>

          {/* Search */}
          {conlangs.length > 0 && (
            <div className="relative mb-6 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
              <Input
                placeholder="Search languages or creators..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-background-paper border-border"
                data-testid="search-public-input"
              />
            </div>
          )}

          {/* Languages Grid */}
          {filteredConlangs.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-conlangs-grid">
              {filteredConlangs.map((conlang) => (
                <Card 
                  key={conlang.conlang_id} 
                  className="bg-background-paper border-border hover:border-primary/50 transition-colors duration-300 cursor-pointer group overflow-hidden"
                  data-testid={`public-conlang-card-${conlang.conlang_id}`}
                  onClick={() => navigate(`/conlang/${conlang.conlang_id}`)}
                >
                  {/* Cover Image */}
                  <div 
                    className="h-28 relative"
                    style={{ background: COVER_IMAGES[conlang.cover_image] || COVER_IMAGES['gradient-purple'] }}
                  >
                    <div className="absolute bottom-2 left-2 flex items-center gap-2">
                      <div className="bg-black/50 rounded-full px-2 py-1 flex items-center gap-1">
                        <User className="w-3 h-3 text-white" />
                        <span className="text-white text-xs">{conlang.owner_name}</span>
                      </div>
                    </div>
                  </div>
                  
                  <CardHeader className="pb-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="font-heading text-xl text-foreground truncate group-hover:text-primary transition-colors">
                        {conlang.name}
                      </CardTitle>
                      {conlang.native_name && (
                        <CardDescription className="font-mono text-secondary mt-1">
                          {conlang.native_name}
                        </CardDescription>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {conlang.description && (
                      <p className="text-foreground-muted text-sm line-clamp-2 mb-3">{conlang.description}</p>
                    )}
                    
                    {/* Stats */}
                    <div className="flex flex-wrap gap-3 mb-3 text-xs text-foreground-subtle">
                      <span className="flex items-center gap-1">
                        <Volume2 className="w-3 h-3" />
                        {conlang.phoneme_count} sounds
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        {conlang.word_count} words
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {conlang.morphology_rules_count} rules
                      </span>
                      <span className="flex items-center gap-1">
                        <GitBranch className="w-3 h-3" />
                        {conlang.syntax_rules_count} syntax
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-foreground-subtle text-xs">
                      <Clock className="w-3 h-3" />
                      <span>Updated {formatDate(conlang.updated_at)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : conlangs.length === 0 ? (
            <div className="text-center py-20" data-testid="empty-public-state">
              <div className="w-20 h-20 rounded-full bg-background-paper border border-border flex items-center justify-center mx-auto mb-6">
                <Globe className="w-10 h-10 text-foreground-subtle" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">No public languages yet</h2>
              <p className="text-foreground-muted mb-6 max-w-md mx-auto">
                Be the first to share a language with the community! Go to your language settings and make it public.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="bg-primary hover:bg-primary/90">
                Go to My Languages
              </Button>
            </div>
          ) : (
            <div className="text-center py-20" data-testid="no-public-results">
              <p className="text-foreground-muted">No languages match your search</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DiscoverPage;
